import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import DemoPlayer from "@/components/feature/DemoPlayer";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import { matches } from "@/mocks/matches";

export default function Live() {
  const liveMatches = matches.filter((m) => m.status === "live");
  const [selectedId, setSelectedId] = useState(liveMatches[0]?.id ?? "");
  const selected = liveMatches.find((m) => m.id === selectedId) ?? liveMatches[0];

  const news = [
    { time: "刚刚", text: "曼城 vs 利物浦下半场进入白热化，福登反超比分" },
    { time: "3 分钟前", text: "国家德比半场战报：皇马 1-1 巴萨，双方对攻激烈" },
    { time: "8 分钟前", text: "欧冠16强：拜仁与巴黎上演进球大战，总比分领先" },
    { time: "15 分钟前", text: "中超今晚焦点战：上海海港主场迎战山东泰山" },
    { time: "22 分钟前", text: "世界杯小组赛前瞻：阿根廷与巴西强强对话" },
  ];

  return (
    <SiteLayout>
      <PageHeader
        title="实时直播中心"
        subtitle="聚合正在进行的足球赛事，点击任意比赛即可切换观看画面，实时掌握场上动态。"
        crumbs={[{ label: "首页", to: "/" }, { label: "实时直播" }]}
        aside={
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 text-white text-sm backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse-live" />
            当前 {liveMatches.length} 场直播进行中
          </div>
        }
      />

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {selected ? (
              <>
                <DemoPlayer
                  title={`${selected.home.name} vs ${selected.away.name}`}
                  cover={selected.cover}
                  isLive
                />

                <div className="mt-5 bg-background-50 border border-background-200 rounded-lg p-5">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <span className="text-xs font-medium text-primary-700 bg-primary-100 px-2.5 py-1 rounded-md">
                      {selected.leagueName} · {selected.round}
                    </span>
                    <StatusBadge status={selected.status} minute={selected.minute} />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <TeamBadge short={selected.home.short} color={selected.home.color} />
                      <span className="text-base md:text-lg font-semibold text-foreground-900 truncate">
                        {selected.home.name}
                      </span>
                    </div>
                    <span className="font-label text-3xl md:text-4xl font-semibold text-foreground-950 tracking-wider shrink-0">
                      {selected.score?.home}
                      <span className="text-foreground-300 mx-1.5">:</span>
                      {selected.score?.away}
                    </span>
                    <div className="flex items-center gap-3 min-w-0 justify-end">
                      <span className="text-base md:text-lg font-semibold text-foreground-900 truncate text-right">
                        {selected.away.name}
                      </span>
                      <TeamBadge short={selected.away.short} color={selected.away.color} />
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-foreground-400 flex items-center gap-2">
                    <i className="ri-map-pin-line" />
                    {selected.venue} · {selected.date} {selected.time}
                  </p>

                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/detail/${selected.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-list-3-line" />
                      查看完整比赛详情
                    </Link>
                    <Link
                      to="/replay"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line" />
                      查看精彩回放
                    </Link>
                  </div>
                </div>

                <p className="mt-3 text-xs text-foreground-400 flex items-start gap-2">
                  <i className="ri-information-line mt-0.5" />
                  提示：当前播放器为前端演示区域，播放与暂停按钮仅作功能示意，暂未接入真实直播流媒体接口。
                </p>
              </>
            ) : (
              <div className="py-20 text-center bg-background-100 rounded-lg border border-background-200">
                <i className="ri-live-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-500">当前没有正在直播的赛事</p>
                <Link
                  to="/"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer"
                >
                  返回首页看看
                </Link>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <h2 className="text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-list-check-2 text-primary-500" />
                正在直播
              </h2>
              <div className="flex flex-col gap-2">
                {liveMatches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedId(m.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                      m.id === selected?.id
                        ? "border-primary-300 bg-primary-50"
                        : "border-background-200 hover:bg-background-100"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs text-primary-700 font-medium">
                        {m.leagueName}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-accent-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse-live" />
                        {m.minute}'
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-foreground-800 truncate">
                        {m.home.name} vs {m.away.name}
                      </span>
                      <span className="font-label text-sm font-semibold text-foreground-950 shrink-0">
                        {m.score?.home}-{m.score?.away}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4">
              <h2 className="text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-newspaper-line text-primary-500" />
                直播资讯
              </h2>
              <ul className="flex flex-col divide-y divide-background-100">
                {news.map((n) => (
                  <li key={n.text} className="py-2.5 flex gap-3">
                    <span className="text-xs text-accent-600 font-medium shrink-0 w-16">
                      {n.time}
                    </span>
                    <span className="text-xs text-foreground-600 leading-relaxed">
                      {n.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}